import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User, Search, ArrowLeft, Users, Phone, Calendar, Shield, BadgeCheck, CheckCircle2, XCircle } from "lucide-react";
import authAxios from "../../api/authAxios";
import { NotificationContext } from "../../context/NotificationContext";

export default function OwnerEmployeeReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [owner, setOwner] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [res, userRes] = await Promise.all([
        authAxios.get(`/api/super-admin/owners/${id}/owner_details/`),
        authAxios.get(`/api/super-admin/owners/${id}/`)
      ]);
      setEmployees(res.data.employees || []);
      setOwner(userRes.data);
    } catch (error) {
      addNotification("Error loading employee data", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(emp => 
    `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) || 
    emp.phone?.includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-bold text-sm uppercase tracking-wider">Back to User Management</span>
        </button>

        <div className="bg-white dark:bg-slate-800 rounded-[3rem] p-8 shadow-xl border border-slate-200 dark:border-slate-700 mb-8 relative overflow-hidden">
            <div className="absolute top-[-20px] right-[-20px] opacity-10">
                <Shield className="w-64 h-64 text-indigo-500" />
            </div>
            
            <div className="relative z-10">
                <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Team & Workforce Report</h1>
                {owner && (
                    <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full text-xs font-black uppercase tracking-widest border border-indigo-500/20">
                            {owner.last_name || owner.business_name}
                        </span>
                        <div className="flex items-center gap-4 text-slate-400 text-sm font-bold ml-2">
                             <div className="flex items-center gap-1"><Users className="w-4 h-4" /> {employees.length} Active Employees</div>
                        </div>
                    </div>
                )}
            </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-[3rem] shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-100 dark:border-slate-700">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                        type="text"
                        placeholder="Search by employee name or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-bold"
                    />
                </div>
            </div>

            <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {loading ? (
                    <div className="col-span-full py-20 flex flex-col items-center justify-center gap-4">
                        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Scanning Workforce...</p>
                    </div>
                ) : filteredEmployees.length === 0 ? (
                    <div className="col-span-full py-20 text-center text-slate-400 font-black uppercase text-xs">No employees found for this owner</div>
                ) : filteredEmployees.map(emp => (
                    <div key={emp.id} className="bg-slate-50 dark:bg-slate-900/40 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-700 relative group transition-all hover:scale-[1.02] hover:shadow-xl shadow-slate-200/50">
                        <div className="flex items-center justify-between mb-8">
                            <div className="w-16 h-16 rounded-2xl bg-indigo-500 text-white flex items-center justify-center font-black shadow-lg shadow-indigo-500/20 text-2xl">
                                {emp.first_name.charAt(0)}
                            </div>
                            <div className={`p-2 rounded-xl flex items-center gap-1 ${emp.is_active ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                {emp.is_active ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                            </div>
                        </div>
                        
                        <h3 className="text-xl font-black text-slate-900 dark:text-white mb-1">{emp.first_name} {emp.last_name}</h3>
                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-6 flex items-center gap-2">
                             <Shield className="w-3 h-3" /> Sales Executive
                        </p>
                        
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 p-3 rounded-2xl shadow-sm">
                                <Phone className="w-4 h-4 text-blue-500" />
                                <span className="text-sm font-bold font-mono">{emp.phone}</span>
                            </div>
                            <div className="flex items-center gap-4 text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 p-3 rounded-2xl shadow-sm">
                                <Calendar className="w-4 h-4 text-orange-500" />
                                <span className="text-xs font-bold uppercase truncate tracking-wider">Joined: {new Date(emp.date_joined).toLocaleDateString()}</span>
                            </div>
                        </div>

                        <div className="mt-8 pt-6 border-t border-slate-200/50 dark:border-slate-700/50 flex justify-between items-center">
                             <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                <BadgeCheck className="w-3 h-3" /> Identity Verified
                             </span>
                             <button className="text-xs font-black text-indigo-600 hover:underline">Track Activity</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
      </div>
    </div>
  );
}
